import { AloftPrice, applyRoundingAndThresholds } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectDeckAdj, selectDeckAdjs } from './selectDeckAdj.ts';
import { selectPatioAdj, selectPatioAdjs } from './selectPatioAdj.ts';
import { selectPorchAdj, selectPorchAdjs } from './selectPorchAdj.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculatePorchPatioDeckAdj = (
  porchAdj: AloftPrice = AloftPrice.zero,
  patioAdj: AloftPrice = AloftPrice.zero,
  deckAdj: AloftPrice = AloftPrice.zero,
) =>
  [porchAdj, patioAdj, deckAdj].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);

const selectPorchPatioDeckAdj = createPriceSelector(
  [selectPorchAdj, selectPatioAdj, selectDeckAdj],
  calculatePorchPatioDeckAdj,
);

const selectPorchPatioDeckAdjs = createSelector(
  [selectPorchAdjs, selectPatioAdjs, selectDeckAdjs, selectCompSequence],
  (porchAdjs, patioAdjs, deckAdjs, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculatePorchPatioDeckAdj(
        porchAdjs[propID],
        patioAdjs[propID],
        deckAdjs[propID],
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedPorchPatioDeckAdj = createPriceSelector(
  [selectPorchPatioDeckAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'porchPatioDeck', adjSettings),
);

export const selectAppliedPorchPatioDeckAdjDisplay = createSelector(
  [selectAppliedPorchPatioDeckAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedPorchPatioDeckAdjsDisplay = createSelector(
  [selectPorchPatioDeckAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentEntities[propID]?.porchPatioDeck;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adjs[propID], 'porchPatioDeck', adjSettings),
          ),
          color: getColorFromAdjustment(adjs[propID]),
        };
      }

      return acc;
    }, {}),
);
